import React, { useEffect, useState } from 'react';
import { Button, SparkIcon, CardSurface } from '@spark';
import { INFERENCE_CHART_OCI_REF, INFERENCE_VALUES_TEMPLATE_URL } from '@/shared/constants/registry';
import CodeBlockWithSelector from './CodeBlockWithSelector';
import LoggingCodeBlock from './LoggingCodeBlock';
import { data_from_server_get } from '@/shared/http/client';
import HighlightedCode from './HighlightedCode';
import { installationCode, specificAICode } from './codeExamples';
import { renderInstallationCode } from './codeRenderers';
import './Documentation.css';

const InstallationDoc = () => {
    const codeVariants = {
        python: installationCode.python,
        typescript: installationCode.typescript,
        go: installationCode.go,
    };

    return (
        <div className="fs-unmask">
            <h1 className="fs-unmask docs-section-title">Installing the SDK</h1>
            <p className="fs-unmask docs-section-subtitle">
                Use the specific_ai SDK to interact with your deployed SpecificAI models.
                Optionally, you can trace and log these interactions.
            </p>
            <h3>Installation</h3>
            <div className="docs-code-card">
                <CodeBlockWithSelector
                    codeVariants={codeVariants}
                    renderCode={(language) => renderInstallationCode(language)}
                    languages={['python', 'typescript', 'go']}
                    showLanguageSelector={true}
                    showProviderSelector={false}
                    showDownloadButton={false}
                    defaultLanguage="python"
                />
            </div>
        </div>
    );
};

const QuickStartDoc = () => (
    <div>
        <h1 className="docs-section-title">Quick Start</h1>
        <CardSurface bordered padding="medium">
            <div className="docs-body-text">
                <p>
                    To get started quickly with the SDK, check out the{' '}
                    <a href="#example-usage" className="docs-link">Example Usage</a>{' '}
                    section for complete code examples in Python and TypeScript.
                </p>
                <p>Here&apos;s a minimal example:</p>
                <ol className="docs-ordered-list">
                    <li><strong>Install the SDK</strong> - See the <a href="#installation" className="docs-link">Installation</a> section</li>
                    <li><strong>Initialize the client</strong> with your SpecificAI platform URL</li>
                    <li><strong>Send a request</strong> to your deployed model</li>
                    <li><strong>Process the response</strong></li>
                </ol>
            </div>
        </CardSurface>
    </div>
);

const ConfigurationDoc = () => (
    <div>
        <h1 className="docs-section-title">Configuration</h1>
        <CardSurface bordered padding="medium">
            <h3>OpenAIHttpClient Configuration Options</h3>
            <ul className="docs-body-text">
                <li><strong>URL</strong>: OpenAI API endpoint (default: https://api.openai.com/v1/chat/completions)</li>
                <li><strong>optuneURL</strong>: Your Optune service URL</li>
                <li><strong>apiKey</strong>: Your OpenAI API key</li>
                <li><strong>modelName</strong>: The model to use (e.g., &quot;gpt-3.5-turbo&quot;)</li>
                <li><strong>useOptuneInference</strong>: Enable/disable Optune&apos;s optimized models</li>
            </ul>

            <h3 style={{ marginTop: 20 }}>Request Options</h3>
            <ul className="docs-body-text">
                <li><strong>usecaseName</strong>: Identifier for your specific use case</li>
                <li><strong>additionalOptions</strong>: Optional OpenAI parameters (temperature, max_tokens, etc.)</li>
                <li><strong>headers</strong>: Optional custom headers for the request</li>
            </ul>
        </CardSurface>
    </div>
);

const QueryingCodeBlock = () => {
    const baseUrl = `${window.location.origin}/`;
    const codeVariants = {
        python: specificAICode.python(baseUrl),
        typescript: specificAICode.typescript(baseUrl),
        go: specificAICode.go(baseUrl),
    };

    return (
        <CodeBlockWithSelector
            codeVariants={codeVariants}
            languages={['python', 'typescript', 'go']}
            codeLanguage={{ python: 'python', typescript: 'typescript', go: 'go' }}
            showLanguageSelector={true}
            showProviderSelector={false}
            defaultLanguage="python"
        />
    );
};

const QueryingDoc = () => (
    <div>
        <h1 className="fs-unmask docs-section-title">Querying</h1>
        <p className="fs-unmask docs-section-subtitle">
            Use the specific_ai SDK to interact with your deployed SpecificAI models.
            Optionally, you can trace and log these interactions into the SpecificAI platform.
        </p>
        <div className="docs-code-card">
            <QueryingCodeBlock />
        </div>
    </div>
);

const LoggingDoc = () => (
    <div>
        <h1 className="fs-unmask docs-section-title">Logging</h1>
        <p className="fs-unmask docs-section-subtitle">
            Log interactions from external (non-SpecificAI) models into the platform.
            Choose a language and task type to see the corresponding script. Records
            are added to an existing Task or used to bootstrap a new one with the same
            task and project name.
        </p>
        <div className="docs-code-card">
            <LoggingCodeBlock />
        </div>
    </div>
);

const HELM_INSTALL_EXAMPLE =
    `helm upgrade --install <RELEASE_NAME> \\\n` +
    `  ${INFERENCE_CHART_OCI_REF} \\\n` +
    `  --version 0.1.0 \\\n` +
    `  -n specificai-inference --create-namespace \\\n` +
    `  -f my-model-values.yaml`;

const ECR_LOGIN_EXAMPLE =
    `aws ecr get-login-password --region us-west-2 \\\n` +
    `  | helm registry login --username AWS --password-stdin \\\n` +
    `    390402537632.dkr.ecr.us-west-2.amazonaws.com`;

const FlowStepHeading = ({ number, title }) => (
    <div className="docs-step-heading">
        <span className="docs-step-badge">{number}</span>
        <h3 style={{ margin: 0 }}>{title}</h3>
    </div>
);

const ExportFormatsTable = () => (
    <table className="docs-table">
        <thead>
            <tr>
                <th>Format</th>
                <th>You get</th>
                <th>Best for</th>
            </tr>
        </thead>
        <tbody>
            <tr>
                <td><strong>Model raw files (ZIP)</strong></td>
                <td>Packaged model files</td>
                <td>Custom integrations</td>
            </tr>
            <tr>
                <td><strong>Docker image</strong></td>
                <td><code>.tar</code> inference image</td>
                <td><code>docker load</code> on your own host</td>
            </tr>
            <tr>
                <td><strong>Push to ECR</strong></td>
                <td>Image in your AWS ECR repo</td>
                <td>Helm deploy on Kubernetes</td>
            </tr>
            <tr>
                <td><strong>Push to GCR</strong></td>
                <td>Image in your GCP Artifact Registry / GCR</td>
                <td>Helm deploy on Kubernetes (GCP)</td>
            </tr>
        </tbody>
    </table>
);

const ExternalDeployFlowCard = () => (
    <CardSurface bordered padding="medium" style={{ marginBottom: 24 }}>
        <h3 style={{ marginTop: 0 }}>Export a model and deploy externally</h3>
        <p className="docs-paragraph--spaced docs-paragraph">
            Start on the <strong>Models</strong> page. Use the same Configure deployment step
            for every format; only the final action differs (save to disk vs push to ECR / GCR).
        </p>

        <FlowStepHeading number={1} title="Choose a format and configure" />
        <p className="docs-paragraph">
            <strong>Models</strong> → model drawer → <strong>Download model</strong> → pick a format → <strong>Next</strong>.
        </p>
        <ExportFormatsTable />
        <div className="docs-paragraph--spaced docs-paragraph">
            In <strong>Configure deployment</strong> set cloud provider, instance type, and device
            (summarization needs <strong>GPU</strong>). For Triton tasks, set prediction strategy and instances if shown.
            <ul className="docs-list">
                <li><strong>ZIP / Docker</strong> - click <strong>Done</strong>, wait for the progress bar, then <strong>Save to disk</strong>.</li>
                <li><strong>Push to ECR</strong> - pick an <strong>ECR target</strong>, click <strong>Push to ECR</strong>, wait for <strong>Image pushed to ECR</strong>. Note the repository URI and tag.</li>
                <li><strong>Push to GCR</strong> - pick a <strong>GCR target</strong> (Account Settings → GCP - GCR), click <strong>Push to GCR</strong>, wait for <strong>Image pushed to GCR</strong>.</li>
            </ul>
        </div>

        <FlowStepHeading number={2} title="Image tag (ECR / GCR push)" />
        <p className="docs-paragraph--spaced docs-paragraph">
            Tags are set automatically: <code>{'{project-name}-{task-name}-v{version}'}</code>
            <br />
            Example: <code>acme-corp-shutdown-reporter-test-v1</code>
        </p>

        <FlowStepHeading number={3} title="Get the Helm values file" />
        <p className="docs-paragraph">
            Use the public template (one file for Triton and vLLM - set <code>runtime</code> inside):
        </p>
        <a href={INFERENCE_VALUES_TEMPLATE_URL} target="_blank" rel="noopener noreferrer">
            <Button
                variant="primary"
                size="medium"
                startIcon={<SparkIcon slug="external-link-icons" size="small" />}
                style={{ marginBottom: 16 }}
            >
                Open values template on GitHub
            </Button>
        </a>
        <div className="docs-paragraph--spaced docs-paragraph">
            Copy it locally (e.g. <code>my-model-values.yaml</code>) and edit at minimum:
            <ul className="docs-list">
                <li><code>runtime</code> - <code>triton</code> (classification, NER, …) or <code>vllm</code> (summarization)</li>
                <li><code>image.repository</code> - your ECR URI without tag</li>
                <li><code>image.tag</code> - tag from Step 1</li>
                <li><code>nodeSelector</code> / <code>tolerations</code> - your GPU node pool</li>
                <li><code>ingress.host</code> and <code>ingress.path</code> - if exposing HTTP(S)</li>
            </ul>
        </div>

        <FlowStepHeading number={4} title="Install the Helm chart" />
        <p className="docs-paragraph">
            Log in to the chart registry (once per shell), then install with your values file:
        </p>
        <HighlightedCode code={ECR_LOGIN_EXAMPLE} language="bash" />
        <div className="docs-spacer" />
        <HighlightedCode code={HELM_INSTALL_EXAMPLE} language="bash" />
        <div className="docs-paragraph" style={{ marginTop: 16 }}>
            When the pod is <code>Ready</code>, check health:
            <ul className="docs-list">
                <li>Triton: <code>/v2/health/ready</code></li>
                <li>vLLM: <code>/health</code></li>
            </ul>
        </div>
    </CardSurface>
);

const ExternalDeployDoc = () => (
    <div className="fs-unmask">
        <h1 className="fs-unmask docs-section-title">External Deploy</h1>
        <p className="fs-unmask docs-section-subtitle">
            Export a trained model from the platform (ZIP, Docker tar, ECR, or GCR push), then deploy it
            to your Kubernetes cluster with the public SpecificAI inference Helm chart.
            The engineering catalog lists this Helm install as a third path alongside in-platform Deploy
            and download/push; the full format list (including Docker Hub and Azure ACR) is in
            <code>docs/features/model-export-and-deploy.md</code>.
        </p>
        <p className="docs-note">
            <strong>Before you start:</strong> for ECR push, confirm{' '}
            <strong>same-account</strong> targets have platform IRSA with ECR push access, or{' '}
            <strong>cross-account</strong> targets have the assume-role configured in Account Settings.
            For <strong>GCP - GCR</strong>, configure Artifact Registry targets (and a service-account /
            WIF JSON for customer projects) under Account Settings.
            Need help?{' '}
            <a href="mailto:support@specific.ai" className="docs-link">
                support@specific.ai
            </a>
        </p>
        <ExternalDeployFlowCard />
    </div>
);

/** Environment variable the client configurations read the key from, as the SDKs do. */
export const MCP_API_KEY_ENV = 'SPECIFIC_AI_API_KEY';
export const MCP_API_KEYS_PATH = '/settings?tab=api-keys';
export const MCP_SERVER_NAME = 'specific-ai';
// Mirrors the templates in backend/app/mcp/resources.py.
export const MCP_RESOURCE_TEMPLATES = [
    { name: 'Task', uri: 'specific-ai://tasks/{name}' },
    { name: 'Trained model', uri: 'specific-ai://models/{task}-v{version}' },
    { name: 'Dataset', uri: 'specific-ai://datasets/{name}' },
];

const mcpCursorConfig = (url) => JSON.stringify(
    { mcpServers: { [MCP_SERVER_NAME]: { url, headers: { Authorization: `Bearer \${env:${MCP_API_KEY_ENV}}` } } } },
    null,
    2,
);

// Single quotes keep the shell from expanding the variable, so Claude Code stores the reference, not the key.
const mcpClaudeCodeCommand = (url) =>
    `claude mcp add --transport http ${MCP_SERVER_NAME} ${url} \\\n` +
    `  --header 'Authorization: Bearer \${${MCP_API_KEY_ENV}}'`;

/** Cursor's one-click install link: the server's config, base64-encoded JSON. */
export const mcpCursorInstallUrl = (url) =>
    `cursor://anysphere.cursor-deeplink/mcp/install?name=${MCP_SERVER_NAME}&config=${btoa(JSON.stringify({ url }))}`;

export const mcpVsCodeInstallUrl = (url) =>
    `vscode:mcp/install?${encodeURIComponent(JSON.stringify({ name: MCP_SERVER_NAME, type: 'http', url }))}`;

// A URL ends before trailing punctuation, and placeholders such as https://<platform address> are not URLs.
const DESCRIPTION_URL = /(https?:\/\/[^\s<>]*[^\s<>.,:;)'"])/;

/** A tool description with each URL in it a link. */
export const linkedDescription = (description) =>
    (description ?? '').split(DESCRIPTION_URL).map((part, index) =>
        index % 2 ? (
            <a key={index} href={part} className="docs-link">
                {part}
            </a>
        ) : (
            part
        ),
    );

const McpDoc = () => {
    const url = `${window.location.origin}/api/mcp`;
    const [tools, setTools] = useState([]);
    const [prompts, setPrompts] = useState([]);
    useEffect(() => {
        data_from_server_get('mcp/tools', (result) => setTools(Array.isArray(result) ? result : [])).catch(() => {});
        data_from_server_get('mcp/prompts', (result) => setPrompts(Array.isArray(result) ? result : [])).catch(() => {});
    }, []);
    return (
        <div className="fs-unmask">
            <h1 className="fs-unmask docs-section-title">MCP Server</h1>
            <p className="fs-unmask docs-section-subtitle">
                Connect an AI agent (Cursor, VS Code, Claude Code, or any MCP client) to this platform. It can
                look up projects, tasks, datasets and trainings, create tasks, upload dataset files from
                your machine or import Hugging Face datasets into them, start trainings, read their
                evaluations, query, deploy or download trained models and delete datasets in your workspace,
                with the same permissions you have in the platform. Every answer suggests the next steps.
            </p>
            <CardSurface bordered padding="medium">
                <FlowStepHeading number={1} title="Add the server to your client" />
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                    <a href={mcpCursorInstallUrl(url)}>
                        <Button variant="primary" size="medium">Add to Cursor</Button>
                    </a>
                    <a href={mcpVsCodeInstallUrl(url)}>
                        <Button variant="secondary" size="medium">Add to VS Code</Button>
                    </a>
                </div>
                <p className="docs-paragraph">Claude Code:</p>
                <HighlightedCode code={`claude mcp add --transport http ${MCP_SERVER_NAME} ${url}`} language="bash" />
                <div className="docs-spacer" />
                <p className="docs-paragraph">
                    Any other client: URL <code>{url}</code>, transport <strong>Streamable HTTP</strong>.
                </p>
                <FlowStepHeading number={2} title="Sign in" />
                <p className="docs-paragraph--spaced docs-paragraph">
                    When the client connects it opens SpecificAI in your browser (in Claude Code, run{' '}
                    <code>/mcp</code> and choose <strong>Authenticate</strong>). Sign in as you do here, then
                    allow the client. This creates an API key for it in the workspace you have open, listed under
                    Settings → API Keys, where revoking it disconnects the client. Its tools act with your role in
                    that workspace, at most Editor, and follow it if it changes; connecting needs the Editor or
                    Admin role there.
                </p>
                <FlowStepHeading number={3} title="Ask your agent" />
                <p className="docs-paragraph">
                    For example: <em>&quot;Which of my SpecificAI tasks are deployed, and is the latest training of
                    the support-triage task finished?&quot;</em> Or pick one of the{' '}
                    <a href="#mcp-prompts" className="docs-link">prompts</a> below from your client (in Cursor
                    and Claude Code, type <code>/</code>).
                </p>
            </CardSurface>
            <div className="docs-spacer" />
            <h3>Use an API key instead</h3>
            <p className="docs-paragraph--spaced docs-paragraph">
                For an agent that cannot open a browser (CI, a server), create a key in{' '}
                <a href={MCP_API_KEYS_PATH} className="docs-link"><strong>Settings</strong> → <strong>API
                Keys</strong></a> in the workspace the agent should see (Editors and Admins can). Treat it like a
                password; it carries your role in that workspace, at most Editor. Set it as the{' '}
                <code>{MCP_API_KEY_ENV}</code> environment variable where your MCP client runs (for
                example <code>export {MCP_API_KEY_ENV}=&lt;key&gt;</code> in your shell profile), send it as{' '}
                <code>Authorization: Bearer &lt;key&gt;</code>, and restart the client.
            </p>
            <p className="docs-paragraph">Cursor (<code>.cursor/mcp.json</code> or <code>~/.cursor/mcp.json</code>):</p>
            <HighlightedCode code={mcpCursorConfig(url)} language="json" />
            <div className="docs-spacer" />
            <p className="docs-paragraph">Claude Code:</p>
            <HighlightedCode code={mcpClaudeCodeCommand(url)} language="bash" />
            <div className="docs-spacer" />
            <h3>Tools</h3>
            <table className="docs-table">
                <thead>
                    <tr>
                        <th>Tool</th>
                        <th>What it does</th>
                    </tr>
                </thead>
                <tbody>
                    {tools.map(({ name, description }) => (
                        <tr key={name}>
                            <td><code>{name}</code></td>
                            <td>{linkedDescription(description)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="docs-spacer" />
            <h3 id="mcp-resources">Resources</h3>
            <p className="docs-paragraph--spaced docs-paragraph">
                Attach a task, a trained model or a dataset of your workspace to a prompt by name, so the agent
                starts from it (in Claude Code, type <code>@</code>; the client completes the names). A trained
                model is named <code>&lt;task&gt;-v&lt;version&gt;</code>, as the Models page shows it.
            </p>
            <table className="docs-table">
                <thead>
                    <tr>
                        <th>Resource</th>
                        <th>URI</th>
                    </tr>
                </thead>
                <tbody>
                    {MCP_RESOURCE_TEMPLATES.map(({ name, uri }) => (
                        <tr key={uri}>
                            <td>{name}</td>
                            <td><code>{uri}</code></td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="docs-spacer" />
            <h3 id="mcp-prompts">Prompts</h3>
            <table className="docs-table">
                <thead>
                    <tr>
                        <th>Prompt</th>
                        <th>What it asks</th>
                        <th>Arguments</th>
                    </tr>
                </thead>
                <tbody>
                    {prompts.map(({ name, title, description, arguments: args }) => (
                        <tr key={name}>
                            <td><strong>{title}</strong><br /><code>{name}</code></td>
                            <td>{linkedDescription(description)}</td>
                            <td>
                                {(args ?? []).map((arg) => (
                                    <div key={arg.name}>
                                        <code>{arg.name}</code>{arg.required ? '' : ' (optional)'} - {arg.description}
                                    </div>
                                ))}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const SupportDoc = () => (
    <div>
        <h1 className="fs-unmask docs-section-title">Support</h1>
        <p className="fs-unmask docs-section-subtitle">
            Need help? Contact our support team at{' '}
            <a href="mailto:support@specific.ai" className="docs-link">
                support@specific.ai
            </a>
        </p>
    </div>
);

const SECTION_COMPONENTS = {
    'quick-start': QuickStartDoc,
    'docs-configuration': ConfigurationDoc,
    'querying': QueryingDoc,
    'logging': LoggingDoc,
    'external-deploy': ExternalDeployDoc,
    'mcp-server': McpDoc,
    'support-doc': SupportDoc,
};

const DOCS_PAGE_SECTIONS = ['installation', 'querying', 'logging', 'external-deploy', 'mcp-server', 'support-doc'];

export function Documentation({ section }) {
    const SectionComponent = SECTION_COMPONENTS[section] || InstallationDoc;
    const isDocsPage = DOCS_PAGE_SECTIONS.includes(section);

    return (
        <div className={`docs-page${isDocsPage ? '' : ' docs-page--inline'}`}>
            <div className="docs-content">
                <SectionComponent />
            </div>
        </div>
    );
}
